import { useCustom, useOne } from '@refinedev/core'
import { Card, Spin, Empty, Typography, Row, Col, Statistic, Button } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons'

const { Text } = Typography

/**
 * 项目概览看板（F5）
 *
 * 数据源：
 *   1. useOne  GET /api/admin/projects/:code  → 项目详情（name/code/status/publicKey...）
 *   2. useCustom  GET /api/admin/:code/stats  → 统计数据（licenses/devices 计数）
 *
 * 展示：
 *   - 顶部 4 张统计卡片（总序列号/活跃/未使用/已吊销）
 *   - 项目信息卡片（名称/code/状态/SKU/解绑上限/离线宽限/创建时间/商品页）
 *   - 公钥信息卡片
 */

/** 后端 /stats 接口返回的扁平结构 */
interface ProjectStats {
  projectCode: string
  totalLicenses: number
  activeLicenses: number
  unusedLicenses: number
  revokedLicenses: number
  totalDevices: number
  recentActivations7d: number
  recentActivations30d: number
}

/** 后端 /projects/:code 接口返回的项目详情 */
interface ProjectInfo {
  id: string
  code: string
  name: string
  description: string | null
  publicKeyB64: string
  itemUrl: string | null
  defaultSku: string
  maxUnbind: number
  offlineGraceDays: number
  minClientVersion: string | null
  status: 'active' | 'suspended' | 'archived'
  createdAt: string
  updatedAt: string
}

export function ProjectOverview({ projectCode }: { projectCode: string }) {
  // 并行拉取项目详情 + 统计数据
  const {
    data: projectData,
    isLoading: projectLoading,
    isError: projectError,
    refetch: refetchProject
  } = useOne<ProjectInfo>({
    resource: 'projects',
    id: projectCode
  })

  const {
    data: statsData,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats
  } = useCustom<ProjectStats>({
    url: `/api/admin/${projectCode}/stats`,
    method: 'get'
  })

  const isLoading = projectLoading || statsLoading
  const isError = projectError || statsError

  if (isLoading) {
    return (
      <div className="app-loading-state" style={{ textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (isError || !projectData?.data || !statsData?.data) {
    return (
      <Card className="app-surface-card">
        <Empty description="加载失败">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              void refetchProject()
              void refetchStats()
            }}
          >
            重试
          </Button>
        </Empty>
      </Card>
    )
  }

  const project = projectData.data
  const stats = statsData.data

  return (
    <div className="app-section-stack page-enter">
      {/* 顶部 4 张统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }} className="stagger-list">
        <Col xs={12} md={6}>
          <Card className="app-kpi-card app-surface-card" style={{ height: '100%' }}>
            <Statistic
              title="总序列号"
              value={stats.totalLicenses}
              prefix={<ClockCircleOutlined style={{ color: '#10B981' }} />}
              valueStyle={{ fontWeight: 600 }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="app-kpi-card app-surface-card" style={{ height: '100%' }}>
            <Statistic
              title="活跃"
              value={stats.activeLicenses}
              valueStyle={{ color: '#10B981', fontWeight: 600 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="app-kpi-card app-surface-card" style={{ height: '100%' }}>
            <Statistic
              title="未使用"
              value={stats.unusedLicenses}
              valueStyle={{ color: '#F59E0B', fontWeight: 600 }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="app-kpi-card app-surface-card" style={{ height: '100%' }}>
            <Statistic
              title="已吊销"
              value={stats.revokedLicenses}
              valueStyle={{ color: '#F43F5E', fontWeight: 600 }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="项目信息" className="app-surface-card">
            <InfoRow label="项目名称" value={project.name} />
            <InfoRow label="项目 code" value={project.code} mono />
            <InfoRow label="状态" value={<StatusText status={project.status} />} />
            <InfoRow label="默认 SKU" value={project.defaultSku} />
            <InfoRow label="解绑上限" value={`${project.maxUnbind}/年`} />
            <InfoRow label="离线宽限" value={`${project.offlineGraceDays} 天`} />
            <InfoRow
              label="创建时间"
              value={new Date(project.createdAt).toLocaleString('zh-CN')}
            />
            {project.itemUrl && (
              <InfoRow
                label="商品页"
                value={
                  <a href={project.itemUrl} target="_blank" rel="noreferrer">
                    查看
                  </a>
                }
              />
            )}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="公钥与设备" className="app-surface-card">
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                公钥（Base64）
              </Text>
              <pre
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 11,
                  padding: 8,
                  background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
                  borderRadius: 4,
                  maxHeight: 80,
                  overflow: 'auto',
                  margin: '4px 0 0',
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {project.publicKeyB64}
              </pre>
            </div>
            <InfoRow label="设备总数" value={String(stats.totalDevices)} />
            <InfoRow label="近 7 天激活" value={String(stats.recentActivations7d)} />
            <InfoRow label="近 30 天激活" value={String(stats.recentActivations30d)} />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

function InfoRow({
  label,
  value,
  mono
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}) {
  return (
    <div className="app-detail-pair">
      <Text type="secondary" className="app-detail-pair__label">
        {label}
      </Text>
      <Text
        className="app-detail-pair__value"
        style={{ fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit' }}
      >
        {value}
      </Text>
    </div>
  )
}

function StatusText({ status }: { status: ProjectInfo['status'] }) {
  const config: Record<ProjectInfo['status'], { color: string; label: string }> = {
    active: { color: '#10B981', label: '运营中' },
    suspended: { color: '#F59E0B', label: '已暂停' },
    archived: { color: '#64748B', label: '已归档' }
  }
  const c = config[status]
  return <span style={{ color: c.color, fontWeight: 500 }}>{c.label}</span>
}
